import { Link } from "react-router-dom";
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowRight, Gift, Share2, Wallet, Sparkles, Mail, Copy,
  UserPlus, CreditCard, Trophy, Check,
} from "lucide-react";
import Testimonials from "../../components/Testimonials";
import CTASection from "../../components/CTASection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { icon: <Share2 size={22} />, t: "Share your link", d: "Get a unique referral link from your dashboard. Share by email, social, or text." },
  { icon: <UserPlus size={22} />, t: "They sign up", d: "When your friend signs up via your link, they get $50 off their first month." },
  { icon: <Wallet size={22} />, t: "You both earn", d: "Once they pay, you get $100 cash credited to your account. Forever, every renewal." },
];

const PERKS = [
  { icon: <Wallet size={22} />, t: "$100 per signup", d: "Earn $100 cash for every paid customer you refer. No caps, no limits." },
  { icon: <Sparkles size={22} />, t: "Recurring rewards", d: "Earn 10% of their subscription forever, as long as they're a Brivo customer." },
  { icon: <Gift size={22} />, t: "Friends get $50 off", d: "Your referrals get $50 off their first month — a generous welcome gift." },
  { icon: <Trophy size={22} />, t: "Tiered bonuses", d: "Refer 5 customers, unlock $500 bonus. Refer 25, unlock $5,000." },
  { icon: <CreditCard size={22} />, t: "Cash, not credits", d: "Withdraw to your bank account anytime. No store-credit nonsense." },
  { icon: <Mail size={22} />, t: "Pre-built emails", d: "Plug-and-play email templates so you don't have to write a single word." },
];

const FAQ = [
  { q: "Who can join the referral program?", a: "Anyone — current Brivo customers, partners, content creators, freelancers. No application required for the standard tier." },
  { q: "When do I get paid?", a: "Earnings appear in your dashboard the moment your referral pays. Withdraw anytime once you cross the $50 threshold." },
  { q: "Is there a cap on earnings?", a: "No. Some of our top referrers earn $5,000+ per month. The more you refer, the more you make." },
  { q: "Can I combine this with the Reseller Program?", a: "If you're a tier 2+ reseller, yes. Talk to your partner manager to enable both." },
];

export default function ReferralProgram() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [referralLink] = useState("https://brivo.app/r/your-name");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("You're in! Check your inbox for your referral link.");
      setEmail("");
    } catch {
      toast.error("Try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard?.writeText(referralLink);
    toast.success("Referral link copied!");
  };

  return (
    <div data-testid="referral-program-page">
      {/* HERO */}
      <section className="relative overflow-hidden hero-gradient grain">
        <div className="absolute -top-32 left-1/4 w-96 h-96 rounded-full bg-[#FFE4E8] opacity-50 blur-3xl animate-blob" />
        <div className="absolute -bottom-32 right-1/4 w-96 h-96 rounded-full bg-[#E8F5E9] opacity-60 blur-3xl animate-blob" style={{ animationDelay: "3s" }} />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20 md:py-28 grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-up">
            <span className="inline-block px-4 py-1.5 rounded-full glass-strong border border-[#1B4332]/10 text-black text-xs font-bold uppercase tracking-widest mb-6">
              Referral Program
            </span>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-extrabold text-black tracking-tight leading-[1.02]">
              Refer a friend.<br />
              <span className="italic text-black/60 font-medium">You both win.</span>
            </h1>
            <p className="mt-7 text-lg text-black/75 leading-relaxed max-w-lg">
              Share Brivo with one business owner. They get $50 off their first month. You get $100 cash — plus 10% of their subscription, forever.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <a href="#start" className="btn-primary">Start earning <ArrowRight size={18} /></a>
              <Link to="/register" className="btn-secondary">Become a customer first</Link>
            </div>
          </div>

          {/* Card mockup */}
          <div className="animate-fade-up delay-200">
            <div className="relative bg-white rounded-3xl shadow-2xl border border-[#1B4332]/8 p-6 md:p-8 max-w-md mx-auto">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-lg bg-[#FFF9E6] flex items-center justify-center text-black">
                    <Gift size={18} />
                  </div>
                  <span className="font-semibold text-black">Your referrals</span>
                </div>
                <span className="text-xs text-[#52B788] font-semibold">+12 this month</span>
              </div>
              <div className="font-display font-extrabold text-5xl text-black tracking-tight">$2,840<span className="text-base font-medium text-black/50"> earned</span></div>

              <div className="mt-6 grid grid-cols-2 gap-3">
                <div className="bg-[#E8F5E9] rounded-2xl p-4">
                  <div className="text-xs text-black/60">Active</div>
                  <div className="font-display font-extrabold text-2xl text-black">28</div>
                </div>
                <div className="bg-[#FFE4E8] rounded-2xl p-4">
                  <div className="text-xs text-black/60">This month</div>
                  <div className="font-display font-extrabold text-2xl text-black">12</div>
                </div>
              </div>

              <div className="mt-6 flex items-center gap-2 px-3 py-2.5 rounded-xl bg-[#FFFCF2] border border-[#1B4332]/10">
                <span className="text-xs text-black/60 truncate flex-1 font-mono">{referralLink}</span>
                <button data-testid="copy-referral-link" onClick={copyLink} className="text-black p-1 hover:bg-[#FFF9E6] rounded">
                  <Copy size={14} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#FFF9E6] text-black text-xs font-semibold mb-4">
              How it works
            </span>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-extrabold text-black tracking-tight">
              Three steps to earning.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {STEPS.map((s, i) => (
              <div key={i} className="bg-[#FFFCF2] rounded-3xl p-8 border border-[#1B4332]/8 card-hover">
                <div className="font-display font-extrabold text-6xl text-black/15 mb-4">{String(i + 1).padStart(2, "0")}</div>
                <div className="w-12 h-12 rounded-xl bg-[#1B4332] flex items-center justify-center text-white mb-5">{s.icon}</div>
                <h3 className="font-display font-bold text-2xl text-black mb-3">{s.t}</h3>
                <p className="text-black/70 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PERKS */}
      <section className="py-24 md:py-32 bg-[#E8F5E9] relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-white/40 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-white/70 backdrop-blur text-black text-xs font-semibold mb-4">
              The perks
            </span>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-extrabold text-black tracking-tight leading-[1.05]">
              Why it pays<br /><span className="italic text-black/60 font-medium">to share Brivo.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PERKS.map((p, i) => (
              <div key={i} className="bg-white rounded-3xl p-7 border border-[#1B4332]/8 card-hover">
                <div className="w-12 h-12 rounded-xl bg-[#FFF9E6] flex items-center justify-center text-black mb-5">{p.icon}</div>
                <h3 className="font-display font-bold text-xl text-black mb-2">{p.t}</h3>
                <p className="text-black/70 leading-relaxed text-sm">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CALCULATOR */}
      <section className="py-24 md:py-32 bg-[#FFE4E8]">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-extrabold text-black tracking-tight">
            Refer just <span className="italic text-black/60 font-medium">5 friends</span> and earn
          </h2>
          <div className="mt-10 font-display font-extrabold text-7xl md:text-8xl lg:text-9xl text-black tracking-tight">
            $1,250<span className="text-3xl md:text-4xl font-medium text-black/50">/yr</span>
          </div>
          <p className="mt-6 text-lg text-black/70 max-w-xl mx-auto">
            Based on $100 signup bonus + 10% of $250/mo Pro plan × 12 months × 5 customers.
          </p>
        </div>
      </section>

      <Testimonials />

      {/* FAQ */}
      <section className="py-24 md:py-28 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <h2 className="font-display text-4xl md:text-5xl font-extrabold text-black tracking-tight">Common questions</h2>
          </div>
          <div className="space-y-3">
            {FAQ.map((f, i) => (
              <details key={i} className="bg-[#FFFCF2] rounded-2xl p-5 group border border-[#1B4332]/8">
                <summary className="font-semibold text-black cursor-pointer flex justify-between items-center">
                  {f.q}
                  <ArrowRight size={18} className="group-open:rotate-90 transition-transform flex-shrink-0 ml-4" />
                </summary>
                <p className="mt-3 text-black/75 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA SIGNUP */}
      <section id="start" className="py-24 md:py-32 bg-[#FFF9E6] relative overflow-hidden">
        <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-white/40 blur-3xl" />
        <div className="relative max-w-3xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-extrabold text-black tracking-tight">
            Get your referral link.
          </h2>
          <p className="mt-5 text-lg text-black/70">
            Drop your email and we'll set you up with a unique link, dashboard, and email templates within minutes.
          </p>
          <form onSubmit={onSubmit} className="mt-9 flex flex-col sm:flex-row gap-3 max-w-lg mx-auto">
            <input
              data-testid="referral-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              className="flex-1 px-5 py-3.5 rounded-xl border border-[#1B4332]/15 focus:border-[#1B4332] focus:outline-none transition-colors bg-white"
            />
            <button data-testid="referral-submit" type="submit" disabled={loading} className="btn-primary justify-center disabled:opacity-60">
              {loading ? "Setting up..." : "Get my link"} <ArrowRight size={18} />
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
